// ===========================================
// MRR Trend Chart Component
// ===========================================
// Line chart showing Monthly Recurring Revenue over time

import { Card, CardContent, Typography, Box } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { RevenueTrendData } from '../../api/reports.api';
import { formatCurrency } from '../../hooks/useReports';

// ===========================================
// TYPES
// ===========================================

interface MRRTrendChartProps {
  data: RevenueTrendData[];
  loading?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function MRRTrendChart({ data, loading = false }: MRRTrendChartProps) {
  if (loading || !data || data.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Monthly Recurring Revenue (MRR)
          </Typography>
          <Box
            sx={{
              height: 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {loading ? 'Loading...' : 'No data available'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Transform data for Recharts (convert cents to dollars).
  // GET /reports/revenue/mrr returns ONE money figure per month — `revenue`,
  // the total of that month's PAID invoices (getMRRTrends). There is no
  // invoiced/paid/outstanding split to plot; this chart previously drew three
  // series from fields the endpoint never sent, so every line was undefined.
  const chartData = data.map((item) => ({
    month: item.monthLabel,
    revenue: item.revenue / 100,
    invoiceCount: item.invoiceCount,
  }));

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Monthly Recurring Revenue (MRR)
        </Typography>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12 }}
            />
            <YAxis
              tickFormatter={(value) => `$${value.toLocaleString()}`}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(value: any, name: any) => {
                if (value === undefined || value === null) return ['N/A', name || ''];
                if (name === 'invoiceCount') {
                  return [value, 'Invoices'];
                }
                return [formatCurrency(Math.round(value * 100)), name || ''];
              }}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#4580E4"
              strokeWidth={2}
              name="Revenue (paid)"
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
